import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { Faq, Testimonial, TinyHouseModel } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { HomeHero } from '@/components/landing/home-hero'
import { FeaturedModels } from '@/components/landing/featured-models'
import { WhySection } from '@/components/landing/why-section'
import { ProcessSteps } from '@/components/landing/process-steps'
import { CustomBuildSection } from '@/components/landing/custom-build-section'
import { TestimonialsSection } from '@/components/landing/testimonials-section'
import { FaqSection } from '@/components/landing/faq-section'
import { CtaBanner } from '@/components/landing/cta-banner'
import { getFaqs, getModels, getTestimonials } from '@/lib/content'

interface HomeProps {
    models: TinyHouseModel[]
    testimonials: Testimonial[]
    faqs: Faq[]
}

const Home: FC<HomeProps> = ({ models, testimonials, faqs }): ReactNode => {
    return (
        <Layout>
            <Seo />

            <HomeHero />
            <FeaturedModels models={models} />
            <WhySection />
            <ProcessSteps />
            <CustomBuildSection />
            <TestimonialsSection testimonials={testimonials} />
            <FaqSection faqs={faqs} />
            <CtaBanner />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<HomeProps> = async () => {
    try {
        const [models, testimonials, faqs] = await Promise.all([
            getModels(),
            getTestimonials(),
            getFaqs()
        ])

        const featured = models.filter(model => model.is_featured)

        return {
            props: {
                models: featured.length > 0 ? featured : models,
                testimonials,
                faqs
            },
            revalidate: 60
        }
    }
    catch (error) {
        console.error('Home page content could not be loaded:', error)

        return {
            props: { models: [], testimonials: [], faqs: [] },
            revalidate: 10
        }
    }
}

export default Home
